/**
 * CategoryMapper Component
 * Maps competitor app categories to FlowIO categories during import
 *
 * Features:
 * - Shows detected categories with transaction counts
 * - Dropdown to select FlowIO category for each
 * - Checkbox to save mapping for future imports
 * - Bulk actions: Auto-map similar names, Skip unmapped
 */
import { useState, useCallback, useMemo, useEffect } from 'react';
import { cn } from '@/utils/cn';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Card from '@/components/ui/Card';
import { useCategories } from '@/hooks/useCategories';
import { useDetectedCategories, useApplyCategoryMappings, useSavedMappings } from '@/hooks';
import type {
  CategoryDetection,
  CategoryMappingInput,
  CompetitorApp,
} from '@/types/document';

// ============================================================================
// Types
// ============================================================================

export interface CategoryMapperProps {
  stagedImportId: string;
  sourceApp: CompetitorApp;
  onComplete: () => void;
  onCancel: () => void;
}

interface MappingState {
  flowioCategoryId: string | null;
  saveForFuture: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format source app name for display
 */
function formatSourceAppName(app: CompetitorApp): string {
  switch (app) {
    case 'mint':
      return 'Mint';
    case 'ynab':
      return 'YNAB';
    case 'quicken':
      return 'Quicken';
    default:
      return app;
  }
}

/**
 * Get confidence badge color based on confidence level
 */
function getConfidenceColor(confidence: number): string {
  if (confidence >= 0.8) return 'bg-green-100 text-green-800';
  if (confidence >= 0.5) return 'bg-yellow-100 text-yellow-800';
  return 'bg-gray-100 text-gray-600';
}

// ============================================================================
// Main Component
// ============================================================================

export function CategoryMapper({
  stagedImportId,
  sourceApp,
  onComplete,
  onCancel,
}: CategoryMapperProps) {
  // Fetch detected categories
  const {
    data: detectedData,
    isLoading: isLoadingCategories,
    error: categoriesError,
  } = useDetectedCategories(stagedImportId);

  // Fetch FlowIO categories for dropdown
  const { data: flowioCategories, isLoading: isLoadingFlowioCategories } = useCategories();

  // Fetch saved mappings for this source app
  const { data: savedMappingsData } = useSavedMappings(sourceApp);

  // Apply mappings mutation
  const applyMappingsMutation = useApplyCategoryMappings();

  // Local state for mapping selections
  const [mappings, setMappings] = useState<Map<string, MappingState>>(new Map());

  // Track which categories have been auto-mapped
  const [autoMappedCount, setAutoMappedCount] = useState(0);

  // Initialize mappings from detected categories and saved mappings
  useEffect(() => {
    if (!detectedData?.categories) return;

    const initialMappings = new Map<string, MappingState>();
    const savedMappingsLookup = new Map<string, string>();

    // Build lookup from saved mappings
    if (savedMappingsData?.mappings) {
      savedMappingsData.mappings.forEach((saved) => {
        savedMappingsLookup.set(saved.sourceCategory?.toLowerCase() ?? '', saved.targetCategoryId ?? '');
      });
    }

    detectedData.categories.forEach((cat) => {
      // Check saved mappings first
      const savedMapping = savedMappingsLookup.get(cat.sourceCategory?.toLowerCase() ?? '');
      if (savedMapping) {
        initialMappings.set(cat.sourceCategory, {
          flowioCategoryId: savedMapping || null,
          saveForFuture: false, // Already saved
        });
      } else if (cat.suggestedFlowioCategoryId) {
        // Use suggested mapping
        initialMappings.set(cat.sourceCategory, {
          flowioCategoryId: cat.suggestedFlowioCategoryId,
          saveForFuture: false,
        });
      } else {
        // No mapping
        initialMappings.set(cat.sourceCategory, {
          flowioCategoryId: null,
          saveForFuture: false,
        });
      }
    });

    setMappings(initialMappings);
  }, [detectedData, savedMappingsData]);

  // Build select options from FlowIO categories
  const categoryOptions = useMemo(() => {
    if (!flowioCategories) return [];

    // Sort by path for hierarchical display
    const sorted = [...flowioCategories].sort((a, b) => (a.path ?? '').localeCompare(b.path ?? ''));

    return [
      { value: '', label: 'Skip (no category)' },
      ...sorted.map((cat) => ({
        value: cat.id,
        // Indent based on depth for hierarchy visualization
        label: (cat.depth ?? 0) > 0 ? `${'  '.repeat(cat.depth ?? 0)}${cat.name ?? ''}` : (cat.name ?? ''),
      })),
    ];
  }, [flowioCategories]);

  // Handle mapping change
  const handleMappingChange = useCallback((sourceCategory: string, flowioCategoryId: string) => {
    setMappings((prev) => {
      const next = new Map(prev);
      const current = next.get(sourceCategory) ?? { flowioCategoryId: null, saveForFuture: false };
      next.set(sourceCategory, {
        ...current,
        flowioCategoryId: flowioCategoryId || null,
      });
      return next;
    });
  }, []);

  // Handle save toggle
  const handleSaveToggle = useCallback((sourceCategory: string, saveForFuture: boolean) => {
    setMappings((prev) => {
      const next = new Map(prev);
      const current = next.get(sourceCategory) ?? { flowioCategoryId: null, saveForFuture: false };
      next.set(sourceCategory, {
        ...current,
        saveForFuture,
      });
      return next;
    });
  }, []);

  // Auto-map similar names
  const handleAutoMap = useCallback(() => {
    if (!flowioCategories || !detectedData?.categories) return;

    // Build a lookup of FlowIO category names (lowercase)
    const flowioLookup = new Map<string, string>();
    flowioCategories.forEach((cat) => {
      flowioLookup.set(cat.name?.toLowerCase() ?? '', cat.id);
      // Also add without special characters
      const simplified = (cat.name?.toLowerCase() ?? '').replace(/[^a-z0-9]/g, '');
      flowioLookup.set(simplified, cat.id);
    });

    let mapped = 0;

    setMappings((prev) => {
      const next = new Map(prev);

      detectedData.categories.forEach((cat) => {
        const current = next.get(cat.sourceCategory);
        // Only auto-map if not already mapped
        if (current && !current.flowioCategoryId) {
          // Try exact match
          const sourceLower = cat.sourceCategory?.toLowerCase() ?? '';
          let match = flowioLookup.get(sourceLower);

          // Try simplified match
          if (!match) {
            const simplified = sourceLower.replace(/[^a-z0-9]/g, '');
            match = flowioLookup.get(simplified);
          }

          // Try partial match (last part of category path)
          if (!match) {
            const parts = sourceLower.split(/[:/]/);
            const lastPart = parts[parts.length - 1];
            if (lastPart) {
              const trimmedLastPart = lastPart.trim();
              match = flowioLookup.get(trimmedLastPart);
              if (!match) {
                match = flowioLookup.get(trimmedLastPart.replace(/[^a-z0-9]/g, ''));
              }
            }
          }

          if (match) {
            next.set(cat.sourceCategory, {
              ...current,
              flowioCategoryId: match,
            });
            mapped++;
          }
        }
      });

      return next;
    });

    setAutoMappedCount(mapped);
  }, [flowioCategories, detectedData]);

  // Skip unmapped categories
  const handleSkipUnmapped = useCallback(() => {
    setMappings((prev) => {
      const next = new Map(prev);
      next.forEach((value, key) => {
        if (!value.flowioCategoryId) {
          // Leave as null - this will skip during import
          next.set(key, { ...value, flowioCategoryId: null });
        }
      });
      return next;
    });
  }, []);

  // Apply mappings and proceed
  const handleApply = useCallback(async () => {
    if (!detectedData?.categories) return;

    const mappingRequests: CategoryMappingInput[] = [];

    detectedData.categories.forEach((cat) => {
      const mapping = mappings.get(cat.sourceCategory);
      if (mapping) {
        mappingRequests.push({
          sourceCategory: cat.sourceCategory,
          flowioCategoryId: mapping.flowioCategoryId,
          saveForFuture: mapping.saveForFuture,
        });
      }
    });

    try {
      await applyMappingsMutation.mutateAsync({
        stagedImportId,
        request: {
          mappings: mappingRequests,
          sourceApp,
        },
      });
      onComplete();
    } catch {
      // Error will be handled by React Query
    }
  }, [detectedData, mappings, stagedImportId, sourceApp, applyMappingsMutation, onComplete]);

  // Calculate stats
  const stats = useMemo(() => {
    let mapped = 0;
    let unmapped = 0;
    let toSave = 0;

    mappings.forEach((value) => {
      if (value.flowioCategoryId) {
        mapped++;
      } else {
        unmapped++;
      }
      if (value.saveForFuture) {
        toSave++;
      }
    });

    return { mapped, unmapped, toSave, total: mappings.size };
  }, [mappings]);

  // Loading state
  if (isLoadingCategories || isLoadingFlowioCategories) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
          <span className="ml-3 text-gray-600">Loading categories...</span>
        </div>
      </Card>
    );
  }

  // Error state
  if (categoriesError) {
    return (
      <Card>
        <div className="text-center py-12">
          <ErrorIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">Failed to Load Categories</h3>
          <p className="mt-2 text-sm text-gray-500">
            There was a problem loading the category data. Please try again.
          </p>
          <div className="mt-6">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // No categories detected
  if (!detectedData?.categories || detectedData.categories.length === 0) {
    return (
      <Card>
        <div className="text-center py-12">
          <CategoryIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No Categories Found</h3>
          <p className="mt-2 text-sm text-gray-500">
            No categories were detected in this import. You can proceed without mapping.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="primary" onClick={onComplete}>
              Continue
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Map {formatSourceAppName(sourceApp)} Categories
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Match your {formatSourceAppName(sourceApp)} categories to FlowIO categories for
              accurate reporting.
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-500">
              <span className="font-medium text-gray-900">{stats.mapped}</span> mapped
            </span>
            <span className="text-gray-500">
              <span className="font-medium text-gray-900">{stats.unmapped}</span> unmapped
            </span>
            <span className="text-gray-500">
              <span className="font-medium text-gray-900">{detectedData.totalTransactions}</span>{' '}
              transactions
            </span>
          </div>
        </div>
      </Card>

      {/* Bulk Actions */}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="secondary" onClick={handleAutoMap}>
          <MagicIcon className="w-4 h-4 mr-2" />
          Auto-map Similar Names
        </Button>
        <Button variant="secondary" onClick={handleSkipUnmapped}>
          <SkipIcon className="w-4 h-4 mr-2" />
          Skip All Unmapped
        </Button>
        {autoMappedCount > 0 && (
          <span className="text-sm text-green-600">
            Auto-mapped {autoMappedCount} categories
          </span>
        )}
      </div>

      {/* Mapping Table */}
      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {formatSourceAppName(sourceApp)} Category
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Transactions
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  FlowIO Category
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Save
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {detectedData.categories.map((cat) => (
                <CategoryMappingRow
                  key={cat.sourceCategory}
                  category={cat}
                  mapping={mappings.get(cat.sourceCategory)}
                  options={categoryOptions}
                  onMappingChange={handleMappingChange}
                  onSaveToggle={handleSaveToggle}
                />
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Actions */}
      <div className="flex justify-between items-center">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <div className="flex items-center gap-3">
          {stats.toSave > 0 && (
            <span className="text-sm text-gray-500">
              {stats.toSave} mapping{stats.toSave !== 1 ? 's' : ''} will be saved for future imports
            </span>
          )}
          <Button
            variant="primary"
            onClick={handleApply}
            isLoading={applyMappingsMutation.isPending}
          >
            Apply Mappings
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Row Component
// ============================================================================

interface CategoryMappingRowProps {
  category: CategoryDetection;
  mapping: MappingState | undefined;
  options: { value: string; label: string }[];
  onMappingChange: (sourceCategory: string, flowioCategoryId: string) => void;
  onSaveToggle: (sourceCategory: string, save: boolean) => void;
}

function CategoryMappingRow({
  category,
  mapping,
  options,
  onMappingChange,
  onSaveToggle,
}: CategoryMappingRowProps) {
  const selectedValue = mapping?.flowioCategoryId ?? '';
  const saveForFuture = mapping?.saveForFuture ?? false;

  return (
    <tr className="hover:bg-gray-50">
      {/* Source Category */}
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900">{category.sourceCategory}</span>
          {category.confidence > 0 && (
            <span
              className={cn(
                'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                getConfidenceColor(category.confidence)
              )}
            >
              {Math.round(category.confidence * 100)}% match
            </span>
          )}
        </div>
        {category.suggestedFlowioCategoryName && !mapping?.flowioCategoryId && (
          <p className="mt-1 text-xs text-gray-500">
            Suggested: {category.suggestedFlowioCategoryName}
          </p>
        )}
      </td>

      {/* Transaction Count */}
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-sm text-gray-600">{category.transactionCount}</span>
      </td>

      {/* FlowIO Category Dropdown */}
      <td className="px-6 py-4">
        <Select
          options={options}
          value={selectedValue}
          onChange={(e) => onMappingChange(category.sourceCategory, e.target.value)}
          placeholder="Select category..."
          aria-label={`FlowIO category for ${category.sourceCategory}`}
        />
      </td>

      {/* Save Checkbox */}
      <td className="px-6 py-4 text-center">
        <input
          type="checkbox"
          checked={saveForFuture}
          onChange={(e) => onSaveToggle(category.sourceCategory, e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          aria-label={`Save mapping for ${category.sourceCategory}`}
          disabled={!mapping?.flowioCategoryId}
          title={
            !mapping?.flowioCategoryId
              ? 'Select a category to enable saving'
              : 'Save this mapping for future imports'
          }
        />
      </td>
    </tr>
  );
}

// ============================================================================
// Icon Components
// ============================================================================

function LoadingSpinner() {
  return (
    <svg
      className="animate-spin h-6 w-6 text-primary-600"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
      />
    </svg>
  );
}

function CategoryIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
    </svg>
  );
}

function MagicIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
      />
    </svg>
  );
}

function SkipIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 8.688c0-.864.933-1.405 1.683-.977l7.108 4.062a1.125 1.125 0 010 1.953l-7.108 4.062A1.125 1.125 0 013 16.81V8.688zM12.75 8.688c0-.864.933-1.405 1.683-.977l7.108 4.062a1.125 1.125 0 010 1.953l-7.108 4.062a1.125 1.125 0 01-1.683-.977V8.688z"
      />
    </svg>
  );
}

export default CategoryMapper;
